import React, { useState, useEffect } from 'react';
import './AffiliateDashboard.css';

function AffiliateDashboard() {
    const [affiliateData, setAffiliateData] = useState(null);
    const [error, setError] = useState(null);
    const [paypalEmail, setPaypalEmail] = useState('');
    const [paypalSaving, setPaypalSaving] = useState(false);
    const [paypalStatus, setPaypalStatus] = useState(null);

    useEffect(() => {
        const fetchAffiliateData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('No token found. Please log in again.');
                }

                // V2 aggregates stats across BOTH iOS (`subscriptions`) and
                // Android (`android_subscriptions`) so this dashboard reflects
                // signups/conversions from both platforms. See getAffiliateDataV2.js.
                const response = await fetch('/.netlify/functions/getAffiliateDataV2', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setAffiliateData(data);
                setPaypalEmail(data.paypal_email || data.email || '');
            } catch (error) {
                console.error('Error fetching affiliate data:', error);
                setError(error.message);
            }
        };

        fetchAffiliateData();
    }, []);

    const handleSavePaypalEmail = async (e) => {
        e.preventDefault();
        setPaypalSaving(true);
        setPaypalStatus(null);
        try {
            const response = await fetch('/.netlify/functions/updatePaypalEmail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ paypal_email: paypalEmail })
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(result.error || `HTTP error! status: ${response.status}`);
            }
            setAffiliateData(prev => ({ ...prev, paypal_email: result.paypal_email }));
            setPaypalEmail(result.paypal_email);
            setPaypalStatus({ type: 'success', message: 'PayPal email saved.' });
        } catch (error) {
            console.error('Error saving PayPal email:', error);
            setPaypalStatus({ type: 'error', message: error.message });
        } finally {
            setPaypalSaving(false);
        }
    };

    if (error) {
        return <div className="error-container">Error: {error}</div>;
    }

    if (!affiliateData) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>Loading your dashboard...</p>
            </div>
        );
    }

    const conversionRate = affiliateData.trials_started
        ? ((affiliateData.conversions / affiliateData.trials_started) * 100).toFixed(1)
        : 0;

    const displayConversionRate = isNaN(conversionRate)
        ? "Share your code with others and get rewarded!"
        : `${conversionRate}%`;

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <div className="welcome-message">{getGreeting()}, {affiliateData.full_name}! 👋</div>
                <h1>Your Affiliate Dashboard</h1>
                <p className="subtitle">Track your performance and earnings in real-time</p>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon">🎯</div>
                    <div className="stat-content">
                        <h3>Active Trials</h3>
                        <div className="stat-value">{affiliateData.trials_started}</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">💎</div>
                    <div className="stat-content">
                        <h3>Converted Users</h3>
                        <div className="stat-value">{affiliateData.active_members}</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">📈</div>
                    <div className="stat-content">
                        <h3>Conversion Rate</h3>
                        <div className={`stat-value ${isNaN(conversionRate) ? 'message' : ''}`}>
                            {displayConversionRate}
                        </div>
                        <p className="stat-label">Trial to Paid</p>
                    </div>
                </div>
            </div>

            <div className="earnings-grid">
                <div className="stat-card earnings-card">
                    <div className="stat-icon">💰</div>
                    <div className="stat-content">
                        <h3>Month to Date Earnings</h3>
                        <div className="stat-value">${affiliateData.month_to_date_earnings}</div>
                        <p className="stat-label">Current Month Earnings</p>
                    </div>
                </div>

                <div className="stat-card earnings-card">
                    <div className="stat-icon">🏦</div>
                    <div className="stat-content">
                        <h3>All Time Earnings</h3>
                        <div className="stat-value">${affiliateData.all_time_earnings}</div>
                        <p className="stat-label">Total Earnings</p>
                    </div>
                </div>
            </div>

            <div className="affiliate-code-card">
                <h2>Your Affiliate Code</h2>
                <div className="code-display">
                    <code>{affiliateData.affiliate_code}</code>
                    <button
                        className="copy-button"
                        onClick={() => {
                            navigator.clipboard.writeText(affiliateData.affiliate_code);
                        }}
                    >
                        Copy
                    </button>
                </div>
                <p className="help-text">Share this code with your audience to track referrals</p>
                <div className="earnings-info">
                    <p>💡 Earn $15 for each converted user you refer! New users get 25% off for using your code.</p>
                </div>
            </div>

            <div className="affiliate-code-card">
                <h2>PayPal Payout Email</h2>
                <form className="paypal-form" onSubmit={handleSavePaypalEmail}>
                    <input
                        type="email"
                        value={paypalEmail}
                        onChange={(e) => {
                            setPaypalEmail(e.target.value);
                            setPaypalStatus(null);
                        }}
                        placeholder={affiliateData.email}
                        aria-label="PayPal email"
                    />
                    <button
                        type="submit"
                        className="copy-button"
                        disabled={paypalSaving || paypalEmail.trim().toLowerCase() === (affiliateData.paypal_email || '').toLowerCase()}
                    >
                        {paypalSaving ? 'Saving...' : 'Save'}
                    </button>
                </form>
                {paypalStatus && (
                    <p className={`paypal-status ${paypalStatus.type}`}>{paypalStatus.message}</p>
                )}
                <p className="help-text">
                    Commissions are paid monthly via PayPal to this address. It starts as your login email;
                    change it if your PayPal account uses a different one, or clear it to go back to your login email.
                </p>
            </div>
        </div>
    );
}

export default AffiliateDashboard;
